import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { EvaluateResultEntry } from "@spoonity/flags-contract";
import { evaluate } from "../api/endpoints";
import { useToast } from "../components/Toast";
import { useWorkspaceEnv } from "../state/WorkspaceEnvContext";

interface AttrRow {
  name: string;
  value: string;
  type: "string" | "number" | "boolean";
}

function toAttributes(rows: AttrRow[]): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const r of rows) {
    if (!r.name) continue;
    if (r.type === "number") out[r.name] = Number(r.value);
    else if (r.type === "boolean") out[r.name] = r.value === "true";
    else out[r.name] = r.value;
  }
  return out;
}

/** Evaluate debugger: key + attributes form → table of flag/treatment/reason/rule (task brief). */
export function EvaluatePage() {
  const { environment } = useWorkspaceEnv();
  const { pushError } = useToast();
  const [params] = useSearchParams();
  const [key, setKey] = useState("member:0001");
  const [rows, setRows] = useState<AttrRow[]>([{ name: "country", value: "CA", type: "string" }]);
  const [results, setResults] = useState<EvaluateResultEntry[] | null>(null);
  const [running, setRunning] = useState(false);

  function updateRow(i: number, patch: Partial<AttrRow>) {
    setRows((cur) => cur.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }

  async function run(e: React.FormEvent) {
    e.preventDefault();
    if (!environment) return;
    setRunning(true);
    try {
      const flag = params.get("flag");
      const res = await evaluate(environment.id, {
        key,
        attributes: toAttributes(rows),
        flags: flag ? [flag] : undefined,
      });
      setResults(res.results);
    } catch (err) {
      pushError(err, "Evaluation failed");
    } finally {
      setRunning(false);
    }
  }

  if (!environment) return null;

  return (
    <div>
      <form className="card" onSubmit={run}>
        <div className="field">
          <label htmlFor="eval-key">Context key</label>
          <input id="eval-key" value={key} onChange={(e) => setKey(e.target.value)} required />
        </div>

        <h3>Attributes</h3>
        {rows.map((r, i) => (
          <div className="row wrap" key={i} style={{ marginBottom: "var(--space-2)" }}>
            <input
              className="select"
              placeholder="attribute"
              aria-label={`attribute name ${i + 1}`}
              value={r.name}
              onChange={(e) => updateRow(i, { name: e.target.value })}
            />
            <select
              className="select"
              aria-label={`attribute type ${i + 1}`}
              value={r.type}
              onChange={(e) => updateRow(i, { type: e.target.value as AttrRow["type"] })}
            >
              <option value="string">string</option>
              <option value="number">number</option>
              <option value="boolean">boolean</option>
            </select>
            {r.type === "boolean" ? (
              <select
                className="select"
                aria-label={`attribute value ${i + 1}`}
                value={r.value}
                onChange={(e) => updateRow(i, { value: e.target.value })}
              >
                <option value="true">true</option>
                <option value="false">false</option>
              </select>
            ) : (
              <input
                className="select"
                type={r.type === "number" ? "number" : "text"}
                aria-label={`attribute value ${i + 1}`}
                value={r.value}
                onChange={(e) => updateRow(i, { value: e.target.value })}
              />
            )}
            <button
              type="button"
              className="btn small danger"
              onClick={() => setRows((cur) => cur.filter((_, idx) => idx !== i))}
            >
              Remove
            </button>
          </div>
        ))}
        <button
          type="button"
          className="btn small"
          onClick={() => setRows((cur) => [...cur, { name: "", value: "", type: "string" }])}
        >
          Add attribute
        </button>

        <div style={{ marginTop: "var(--space-4)" }}>
          <button className="btn primary" type="submit" disabled={running}>
            {running ? "Evaluating…" : "Evaluate"}
          </button>
        </div>
      </form>

      {results && (
        <table className="table">
          <thead>
            <tr>
              <th>Flag</th>
              <th>Treatment</th>
              <th>Reason</th>
              <th>Rule</th>
              <th>Config</th>
            </tr>
          </thead>
          <tbody>
            {results.map((r) => (
              <tr key={r.flag}>
                <td>{r.flag}</td>
                <td>
                  <strong>{r.treatment}</strong>
                </td>
                <td>{r.reason}</td>
                <td>{r.ruleId ?? <span className="muted">—</span>}</td>
                <td>
                  <code>{r.config ?? "—"}</code>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
